/**
=========================================================
* BASE: Learning Repository - v1.0.0
=========================================================

* Copyright 2022 Sean Dooley (https://www.dooley-dev.com)

Coded by www.dooley-dev.com

 =========================================================

* The above copyright notice shall be included in substantial portions of the Software.
*/

import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import history from "../../../../@history";
import LessonModel from "../model/LessonModel";
import { showMessage } from "../../../store/BASE/messageSlice";

export const getLesson = createAsyncThunk(
  "lessons/getLesson",
  async (id, { dispatch, getState }) => {
    try {
      // send request to get lesson
      const response = await axios.get(
        `${process.env.REACT_APP_API_SERVICE_URL}/api/lessons/${id}`
      );
      // get response data
      const data = await response.data;
      // return data
      return data;
    } catch (error) {
      // if error, redirect to lessons page
      history.push({ pathname: `/lessons` });
      // show error message
      dispatch(showMessage({ message: "Lesson Not Found", variant: "error" }));
      // return null
      return null;
    }
  }
);

export const getFullLesson = createAsyncThunk(
  "lessons/getFullLesson",
  async (id, { dispatch, getState }) => {
    try {
      // send request to get lesson
      const response = await axios.get(
        `${process.env.REACT_APP_API_SERVICE_URL}/api/lessons/${id}`
      );
      // define response data
      const data = await response.data;
      // get course data
      const courseResponse = await axios.get(
        `${process.env.REACT_APP_API_SERVICE_URL}/api/courses/${data.course_id}`
      );
      // define course data
      const courseData = await courseResponse.data;
      // add extra data to the response
      data.course = courseData;

      // return data
      return data;

    } catch (error) {
      // if error, redirect to lessons page
      history.push({ pathname: `/imi-comments` });
      // show error message
      dispatch(showMessage({ message: "Lesson Not Found", variant: "error" }));
      // return null
      return null;
    }
  }
);

export const addLesson = createAsyncThunk(
  "lessons/addLesson",
  async (lesson, { dispatch, getState }) => {
    try {
      // set user id
      lesson.user_id = getState().user.id;
      // send request to add lesson
      const response = await axios.post(
        `${process.env.REACT_APP_API_SERVICE_URL}/api/lessons`,
        lesson
      );
      // get response data
      const data = await response.data;
      // show success message
      dispatch(showMessage({ message: "Lesson Added" }));
      // return data
      return data;
    } catch (error) {
      // show error message
      dispatch(showMessage({ message: "Lesson Not Added", variant: "error" }));
      // return null
      return null;
    }
  }
);

export const updateLesson = createAsyncThunk(
  "lessons/updateLesson",
  async (lesson, { dispatch, getState }) => {
    try {
      // set user id
      lesson.user_id = getState().user.id;
      // send request to update lesson
      const response = await axios.patch(
        `${process.env.REACT_APP_API_SERVICE_URL}/api/lessons/${lesson.id}`,
        lesson
      );
      // get response data
      const data = await response.data;
      // show success message
      dispatch(showMessage({ message: "Lesson Updated" }));
      // return data
      return data;
    } catch (error) {
      // show error message
      dispatch(showMessage({ message: "Lesson Not Updated", variant: "error" }));
      // return null
      return null;
    }
  }
);

export const removeLesson = createAsyncThunk(
  "lessons/removeLesson",
  async (id, { dispatch, getState }) => {
    try {
      // send request to remove lesson
      const response = await axios.delete(
        `${process.env.REACT_APP_API_SERVICE_URL}/api/lessons/${id}`
      );
      // get response data
      const data = await response.data;
      // show success message
      dispatch(showMessage({ message: "Lesson Removed" }));
      // return data
      return data;
    } catch (error) {
      // show error message
      dispatch(showMessage({ message: "Lesson Not Removed", variant: "error" }));
      // return null
      return null;
    }
  }
);

// define selector
export const selectLesson = ({ lessons }) => lessons.instance;

export const selectFullLesson = ({ lessons }) => lessons.instance;

// define slice
const lessonSlice = createSlice({
  name: "lesson",
  initialState: null,
  reducers: {
    newLesson: (state, action) => LessonModel(),
    resetLesson: () => null,
  },
  extraReducers: {
    [getLesson.pending]: (state, action) => null,
    [getLesson.fulfilled]: (state, action) => action.payload,
    [getFullLesson.pending]: (state, action) => null,
    [getFullLesson.fulfilled]: (state, action) => action.payload,
    [updateLesson.fulfilled]: (state, action) => action.payload,
    [removeLesson.fulfilled]: (state, action) => null,
  },
});

// export actions
export const { resetLesson, newLesson } = lessonSlice.actions;

export default lessonSlice.reducer;
